import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { useMemo } from "react";

import { calculateCompleteness } from "@/helpers/realEstateDataValidationAndCompletenessHelpers";
import { OneClickPropertyDetails } from "@/pages/oneClickPage/oneClickPageSlice";
import { PropertyInformation as RealEstatePropertyInformation } from "@/types/refineRealEstate/refineRealEstate";

interface CompletenessIndicatorProps {
  realEstateState?: RealEstatePropertyInformation | OneClickPropertyDetails;
  size?: number;
}

export const CompletenessIndicator = ({ realEstateState, size = 75 }: CompletenessIndicatorProps): JSX.Element => {
  const theme = useTheme();

  const getCompletenessColor = (percentage: number): string => {
    if (percentage >= 90) return theme.semanticColors.completeness.high;
    if (percentage >= 75) return theme.semanticColors.completeness.medium;
    return theme.semanticColors.completeness.low;
  };

  const completenessData = useMemo(() => {
    const percentage = realEstateState ? calculateCompleteness(realEstateState) : 0;

    return {
      percentage,
      color: getCompletenessColor(percentage),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realEstateState]);

  return (
    <Box sx={{ position: "relative", width: `${size}px`, height: `${size}px` }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={5}
        sx={{ color: theme.semanticColors.completeness.track, position: "absolute" }}
      />
      <CircularProgress
        variant="determinate"
        value={completenessData.percentage}
        size={size}
        thickness={5}
        sx={{ color: completenessData.color }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ fontWeight: 500, fontSize: `${size / 3.5}px`, color: theme.semanticColors.text.subtle }}>
          {`${completenessData.percentage}%`}
        </Typography>
      </Box>
    </Box>
  );
};
